import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useCalendlyBooking } from '../../../hooks/useCalendlyBooking';

const FinalCTASection = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 47,
    seconds: 32
  });

  const { openCalendlyWidget } = useCalendlyBooking();

  const handleDirectCalendlyBooking = () => {
    // Track high-intent user from landing page
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'calendly_landing_page_cta', {
        event_category: 'landing_page',
        event_label: 'final_cta_calendly',
        value: 1
      });
    }

    // Open Calendly widget
    openCalendlyWidget({}, '30min');
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        let { hours, minutes, seconds } = prevTime;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          // Reset timer
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const urgencyMessages = [
    "Cada día que esperas, tu cuerpo envejece más",
    "Tu edad real aumenta mientras lees esto",
    "¿Cuánto tiempo más vas a postergar tu salud?",
    "El momento perfecto no existe, pero AHORA sí"
  ];

  const [currentMessage, setCurrentMessage] = useState(0);

  useEffect(() => {
    const messageTimer = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % urgencyMessages?.length);
    }, 3000);

    return () => clearInterval(messageTimer);
  }, [urgencyMessages?.length]);

  return (
    <section className="py-20 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent transform rotate-12 scale-150" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Urgency Timer */}
          <div className="inline-flex items-center px-6 py-3 bg-destructive/10 border-2 border-destructive/20 rounded-full mb-8">
            <Icon name="Clock" size={20} className="text-destructive mr-2" />
            <span className="font-body-semibold text-destructive">
              Evaluación gratuita disponible por:
            </span>
            <div className="ml-4 flex items-center space-x-2">
              <div className="bg-destructive text-destructive-foreground px-2 py-1 rounded font-mono text-sm">
                {String(timeLeft?.hours)?.padStart(2, '0')}
              </div>
              <span className="text-destructive">:</span>
              <div className="bg-destructive text-destructive-foreground px-2 py-1 rounded font-mono text-sm">
                {String(timeLeft?.minutes)?.padStart(2, '0')}
              </div>
              <span className="text-destructive">:</span>
              <div className="bg-destructive text-destructive-foreground px-2 py-1 rounded font-mono text-sm">
                {String(timeLeft?.seconds)?.padStart(2, '0')}
              </div>
            </div>
          </div>

          {/* Main Headline */}
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
            ¿Vas a Seguir{' '}
            <span className="text-destructive font-heading-bold">Mintiéndote</span>{' '}
            a Ti Misma?
          </h2>

          {/* Rotating Urgency Messages */}
          <div className="h-16 flex items-center justify-center mb-8">
            <p className="text-xl md:text-2xl text-accent font-body-semibold animate-pulse">
              {urgencyMessages?.[currentMessage]}
            </p>
          </div>

          {/* Main CTA */}
          <div className="space-y-6 mb-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/edad-real-assessment-quiz">
                <Button
                  variant="default"
                  size="xl"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-card-elevated hover-scale text-xl px-12 py-6 rounded-2xl"
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={24}
                >
                  <span className="font-heading-bold">
                    DESCUBRE TU EDAD REAL AHORA
                  </span>
                </Button>
              </Link>

              <Button
                variant="outline"
                size="xl"
                className="border-accent text-accent hover:bg-accent/10 text-xl px-12 py-6 rounded-2xl"
                onClick={handleDirectCalendlyBooking}
                iconName="Calendar"
                iconPosition="left"
                iconSize={24}
              >
                <span className="font-heading-bold">
                  RESERVAR CONSULTA DIRECTA
                </span>
              </Button>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span className="font-caption text-sm">Quiz: 5 min • Consulta: 30 min</span>
              </div>
              <div className="hidden sm:block w-1 h-1 bg-muted-foreground rounded-full" />
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} />
                <span className="font-caption text-sm">100% Gratuito</span>
              </div>
              <div className="hidden sm:block w-1 h-1 bg-muted-foreground rounded-full" />
              <div className="flex items-center space-x-2">
                <Icon name="Lock" size={16} />
                <span className="font-caption text-sm">Datos Seguros</span>
              </div>
            </div>
          </div>

          {/* Calendly Information */}
          <div className="bg-card rounded-3xl shadow-card-elevated border border-border p-6 max-w-2xl mx-auto mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Icon name="ExternalLink" size={20} className="text-accent" />
              <span className="font-body-semibold text-accent">
                Calendario Real de Miri Espejo
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Acceso directo a{' '}
              <span className="font-mono text-primary">calendly.com/comeyvivesano-info/30min</span>
              <br />
              Tu consulta será reservada directamente en el calendario oficial.
            </p>
          </div>

          {/* Fear of Missing Out */}
          <div className="bg-card rounded-3xl shadow-card-elevated border border-border p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-left">
                <h3 className="font-heading text-2xl text-foreground mb-4">
                  Lo que Pierdes Cada Día que Esperas:
                </h3>
                <ul className="space-y-3">
                  {[
                    "Tu metabolismo se ralentiza más",
                    "Tus niveles de energía siguen bajando",
                    "El daño celular se acumula",
                    "La ventana de recuperación se cierra"
                  ]?.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Icon name="AlertTriangle" size={16} className="text-destructive mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="text-left">
                <h3 className="font-heading text-2xl text-foreground mb-4">
                  Lo que Ganas Actuando HOY:
                </h3>
                <ul className="space-y-3">
                  {[
                    "Conoces tu situación real",
                    "Plan personalizado inmediato",
                    "Acceso a programa exclusivo",
                    "Consulta con Miri Espejo"
                  ]?.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Final Push */}
          <div className="mt-12 space-y-4">
            <p className="text-lg text-foreground font-body-semibold">
              Más de 2,847 mujeres ya conocen su edad real.
            </p>
            <p className="text-xl text-accent font-heading">
              ¿Vas a ser la próxima o vas a seguir posponiendo tu salud?
            </p>
            
            {/* Social Proof Counter */}
            <div className="flex items-center justify-center space-x-4 mt-8">
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=32&h=32&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=32&h=32&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face"
                ]?.map((src, index) => (
                  <div key={index} className="w-8 h-8 rounded-full border-2 border-card overflow-hidden">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-body-semibold text-accent">+127</span> mujeres completaron su evaluación en las últimas 24 horas
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;